import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, message, Modal, Form, Input, InputNumber, Radio, Space, Typography, Drawer, Avatar } from 'antd';
import { SearchOutlined, PlusCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { wxMemberApi } from '../../services/api';

const { Title } = Typography;

const WxMembers = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [keyword, setKeyword] = useState('');

  // 调整积分弹窗
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // 积分记录抽屉
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordUser, setRecordUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [recordLoading, setRecordLoading] = useState(false);

  useEffect(() => {
    fetchData(1);
  }, []);

  const fetchData = async (page = 1, searchKeyword = keyword) => {
    setLoading(true);
    try {
      const res = await wxMemberApi.list({ page, limit: 10, keyword: searchKeyword });
      setData(res.data.list);
      setPagination({ ...pagination, current: page, total: res.data.pagination.total });
    } catch (err) {
      message.error(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchData(1, keyword);
  };

  const openAdjust = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({ action: 'add', points: 100, description: '' });
    setAdjustOpen(true);
  };

  const handleAdjust = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const res = await wxMemberApi.adjustPoints(editing.id, values);
      message.success(res.message || '调整成功');
      setAdjustOpen(false);
      fetchData(pagination.current);
    } catch (err) {
      message.error(err.message || '调整失败');
    } finally {
      setSaving(false);
    }
  };

  const openRecords = async (record) => {
    setRecordUser(record);
    setRecordOpen(true);
    setRecordLoading(true);
    try {
      const res = await wxMemberApi.transactions(record.id);
      setTransactions(res.data || []);
    } catch (err) {
      message.error(err.message || '加载记录失败');
    } finally {
      setRecordLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '头像', dataIndex: 'avatar_url', width: 60,
      render: (v, r) => v ? <Avatar src={v} /> : <Avatar style={{ background: '#A98E4E' }}>{(r.nickname || '?')[0]}</Avatar>
    },
    { title: '昵称', dataIndex: 'nickname', width: 120, render: (v) => v || '-' },
    { title: '会员号', dataIndex: 'member_no', width: 130 },
    { title: '手机号', dataIndex: 'phone', width: 120, render: (v) => v || '-' },
    {
      title: '积分', dataIndex: 'points', width: 100,
      render: (v) => <span style={{ color: '#A98E4E', fontWeight: 600 }}>{v}</span>
    },
    {
      title: '注册时间', dataIndex: 'created_at', width: 150,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作', width: 200, render: (_, r) => (
        <Space>
          <Button type="link" size="small" icon={<PlusCircleOutlined />} onClick={() => openAdjust(r)}>
            调整积分
          </Button>
          <Button type="link" size="small" onClick={() => openRecords(r)}>
            积分记录
          </Button>
        </Space>
      )
    },
  ];

  const txColumns = [
    {
      title: '时间', dataIndex: 'created_at', width: 150,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '类型', dataIndex: 'type', width: 70,
      render: (v) => v === 'earn' ? <Tag color="gold">收入</Tag> : <Tag>支出</Tag>
    },
    {
      title: '积分', dataIndex: 'points', width: 90,
      render: (v, r) => (
        <span style={{ color: r.type === 'earn' ? '#A98E4E' : '#999', fontWeight: 600 }}>
          {r.type === 'earn' ? '+' : '-'}{v}
        </span>
      )
    },
    { title: '余额', dataIndex: 'balance', width: 80 },
    { title: '说明', dataIndex: 'description' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>会员管理</Title>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="搜索昵称 / 手机号 / 会员号"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={handleSearch}
          onPressEnter={handleSearch}
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
          allowClear
        />
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 位会员`,
        }}
        onChange={(p) => fetchData(p.current)}
      />

      {/* 调整积分弹窗 */}
      <Modal
        title={`调整积分 — ${editing?.nickname || editing?.member_no || ''}`}
        open={adjustOpen}
        onCancel={() => setAdjustOpen(false)}
        onOk={handleAdjust}
        confirmLoading={saving}
        okText="确认调整"
        cancelText="取消"
        okButtonProps={{ style: { background: '#A98E4E' } }}
      >
        <div style={{ marginBottom: 16, color: '#999' }}>
          当前积分：<span style={{ color: '#A98E4E', fontSize: 20, fontWeight: 600 }}>{editing?.points ?? '-'}</span>
        </div>
        <Form form={form} layout="vertical">
          <Form.Item name="action" label="操作" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="add">增加</Radio.Button>
              <Radio.Button value="deduct">扣除</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="points" label="积分数量" rules={[{ required: true, message: '请输入积分数量' }]}>
            <InputNumber min={1} style={{ width: '100%' }} placeholder="正整数" />
          </Form.Item>
          <Form.Item name="description" label="说明（选填）">
            <Input placeholder="如：门店消费赠送 / 活动奖励 / 修正误差" maxLength={100} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 积分记录抽屉 */}
      <Drawer
        title={`积分记录 — ${recordUser?.nickname || recordUser?.member_no || ''}`}
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        width={680}
      >
        <Table
          columns={txColumns}
          dataSource={transactions}
          rowKey="id"
          loading={recordLoading}
          size="small"
          pagination={false}
        />
      </Drawer>
    </div>
  );
};

export default WxMembers;
